import React, { useState } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { DiscountCard } from './DiscountCard';
import { IManagedEventDiscount, ICreateDiscountCode } from '../../../../redux/events/types';
import CustomButton from '../../../Button/CustomButton';
import { CreateDiscountDialog } from './CreateDiscountDialog';
import { useDispatch } from 'react-redux';
import { createDiscountCode, updateDiscountCode, deleteDiscountCode } from '../../../../redux/events/action';
import { showToastMessage } from '../../../../utils/AppUtils';

interface EventDiscountsTabProps {
    discounts?: IManagedEventDiscount;
    eventId: string;
}

export const EventDiscountsTab: React.FC<EventDiscountsTabProps> = ({ discounts, eventId }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [selectedDiscount, setSelectedDiscount] = useState<IManagedEventDiscount['data'][0] | null>(null);

    const handleCreateDiscount = (discountData: ICreateDiscountCode) => {
        dispatch(
            createDiscountCode({
                ...discountData,
                event: eventId,
            } as ICreateDiscountCode),
        );
        setIsCreateDialogOpen(false);
    };

    const handleUpdateDiscount = (id: string, discountData: ICreateDiscountCode) => {
        dispatch(updateDiscountCode({ id, body: discountData }));
    };

    const handleDeleteDiscount = (id: string) => {
        dispatch(deleteDiscountCode(id));
    };

    const handleMoreAction = (actionType: string, discountId: string) => {
        const discount = discounts?.data.find(d => d._id === discountId);
        if (!discount) return;

        switch (actionType) {
            case 'edit':
                setSelectedDiscount(discount);
                setIsCreateDialogOpen(true);
                break;
            case 'activate':
            case 'deactivate': {
                const isActivating = actionType === 'activate';
                if (isActivating) {
                    // Check if expiration date is in the future
                    const expirationDate = new Date(discount.expiration_date);
                    if (expirationDate < new Date()) {
                        showToastMessage('Cannot activate discount: Expiration date has passed', 'error');
                        return;
                    }
                }

                dispatch(
                    updateDiscountCode({
                        id: discountId,
                        body: {
                            ...discount,
                            active: isActivating,
                            code_type: discount.code_type as 'discount' | 'coupon' | 'reedemable',
                            discount_type: discount.discount_type as 'percentage' | 'fixed',
                            discount_structure: discount.discount_structure as 'one_time' | 'time_period',
                        },
                    }),
                );
                break;
            }
            case 'delete':
                handleDeleteDiscount(discountId);
                break;
            default:
                break;
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: '1.25rem',
                        color: theme[themeStyle].primary_white,
                    }}>
                    Discounts
                </Typography>
                <CustomButton
                    customStyle={{
                        borderRadius: 5,
                    }}
                    buttonProps={{
                        startIcon: <Add />,
                        onClick: () => {
                            setSelectedDiscount(null);
                            setIsCreateDialogOpen(true);
                        },
                        sx: {
                            backgroundColor: theme[themeStyle].highlight,
                            color: theme[themeStyle].black,

                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            '&:hover': {
                                backgroundColor: theme[themeStyle].primary_orange,
                                opacity: 0.9,
                            },
                            px: 2,
                            py: 1,
                        },
                    }}
                    label="Create Discount"
                />
            </Box>

            {!discounts?.data || discounts.data.length === 0 ? (
                <Box
                    sx={{
                        backgroundColor: theme[themeStyle].secondaryBg,
                        borderRadius: 2,
                        padding: 4,
                        textAlign: 'center',
                    }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            fontSize: '1rem',
                            color: theme[themeStyle].primary_white,
                        }}>
                        No discounts have been created yet
                    </Typography>
                </Box>
            ) : (
                <Grid container spacing={2}>
                    {discounts.data.map(discount => (
                        <Grid item xs={12} md={6} lg={4} key={discount._id}>
                            <DiscountCard discount={discount} onMoreAction={handleMoreAction} />
                        </Grid>
                    ))}
                </Grid>
            )}

            <CreateDiscountDialog
                open={isCreateDialogOpen}
                onClose={() => {
                    setIsCreateDialogOpen(false);
                    setSelectedDiscount(null);
                }}
                onSubmit={handleCreateDiscount}
                onUpdate={handleUpdateDiscount}
                onDelete={handleDeleteDiscount}
                eventId={eventId}
                editDiscount={selectedDiscount || undefined}
            />
        </Box>
    );
};
